import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { History, RefreshCcw } from 'lucide-react';
import { EmptyState } from '../../../../components/admin/EmptyState';
import type {
  AdminActionHistoryItem,
  AdminActionStatus,
} from '../../../../services/adminAgent.service';
import { formatDateTime } from '../../../../utils/dateFormat';
import { useDebouncedValue } from '../../docs/utils/useDebouncedValue';
import { ActionHistoryDetailDialog } from '../components/ActionHistoryDetailDialog';
import { ACTION_STATUS_STYLES } from '../components/actionStatusStyles';
import { useAdminActionHistory } from '../hooks/useAdminActionHistory';

const SEARCH_DEBOUNCE_MS = 350;
const PAGE_SIZE_OPTIONS = [10, 20, 50];
const STATUSES: AdminActionStatus[] = [
  'pending',
  'approved',
  'executed',
  'failed',
  'rejected',
  'expired',
];

const COLUMNS: GridColDef<AdminActionHistoryItem>[] = [
  {
    field: 'createdAt',
    headerName: 'Created',
    width: 160,
    sortable: false,
    valueFormatter: (value: string) => formatDateTime(value, true),
  },
  {
    field: 'operationId',
    headerName: 'Operation',
    flex: 1,
    minWidth: 240,
    sortable: false,
    renderCell: (params) => (
      <div className="min-w-0 py-1">
        <div className="truncate text-sm font-medium text-gray-900 dark:text-slate-100">
          {params.row.operationId}
        </div>
        <div className="truncate font-mono text-xs text-gray-500 dark:text-slate-400">
          {params.row.path}
        </div>
      </div>
    ),
  },
  {
    field: 'riskLevel',
    headerName: 'Risk',
    width: 110,
    sortable: false,
    renderCell: (params) => (
      <span className={params.value === 'destructive' ? 'font-medium text-red-600' : ''}>
        {params.value}
      </span>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 110,
    sortable: false,
    renderCell: (params) => (
      <span
        className={`rounded-full px-2 py-0.5 text-xs ${ACTION_STATUS_STYLES[params.value as AdminActionStatus]}`}
      >
        {params.value}
      </span>
    ),
  },
  {
    field: 'requestedBy',
    headerName: 'Requested by',
    width: 200,
    sortable: false,
    valueGetter: (_value, row) => row.requestedBy.email,
  },
  {
    field: 'approver',
    headerName: 'Decided by',
    width: 200,
    sortable: false,
    valueGetter: (_value, row) => row.approver?.email ?? '—',
  },
];

export const AdminAgentHistoryPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[1]);
  const [status, setStatus] = useState<AdminActionStatus | ''>('');
  const [risk, setRisk] = useState<'write' | 'destructive' | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [selected, setSelected] = useState<AdminActionHistoryItem | null>(null);
  const search = useDebouncedValue(searchInput.trim(), SEARCH_DEBOUNCE_MS);

  const { data, isLoading, isFetching, error, refetch } = useAdminActionHistory({
    page: page + 1,
    pageSize,
    status: status || undefined,
    riskLevel: risk || undefined,
    search: search || undefined,
  });
  const items = data?.items ?? [];

  const resetPageThen =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setPage(0);
      setter(value);
    };

  return (
    <Box className="min-h-screen bg-slate-50 p-4 dark:bg-slate-900 sm:p-6">
      <Box className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <Box>
          <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
            Assistant History
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Every change the admin assistant proposed: who asked, who approved, and what happened.
          </Typography>
        </Box>
        <Box className="flex flex-wrap items-center gap-3">
          <TextField
            size="small"
            label="Search operation or path"
            value={searchInput}
            onChange={(event) => resetPageThen(setSearchInput)(event.target.value)}
            className="w-56 bg-white dark:bg-slate-800"
          />
          <FormControl size="small" className="w-36 bg-white dark:bg-slate-800">
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(event) =>
                resetPageThen(setStatus)(event.target.value as AdminActionStatus | '')
              }
            >
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" className="w-36 bg-white dark:bg-slate-800">
            <InputLabel>Risk</InputLabel>
            <Select
              value={risk}
              label="Risk"
              onChange={(event) =>
                resetPageThen(setRisk)(event.target.value as 'write' | 'destructive' | '')
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="write">write</MenuItem>
              <MenuItem value="destructive">destructive</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshCcw size={16} />}
            onClick={() => refetch()}
            className="h-10"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Box className="rounded-xl bg-white shadow-sm dark:bg-slate-800">
        {isLoading ? (
          <Box className="flex items-center justify-center p-12">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box className="p-12">
            <Typography className="text-center text-red-600 dark:text-red-400">
              Failed to load the history. Please try again.
            </Typography>
          </Box>
        ) : items.length > 0 ? (
          <DataGrid
            rows={items}
            columns={COLUMNS}
            getRowId={(row) => row.publicId}
            loading={isFetching}
            disableRowSelectionOnClick
            disableColumnMenu
            paginationMode="server"
            rowCount={data?.total ?? 0}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            onRowClick={(params) => setSelected(params.row)}
            getRowHeight={() => 'auto'}
            autoHeight
            className="border-0"
            sx={{
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderColor: 'divider' },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'background.default',
                borderBottom: '2px solid',
                borderColor: 'divider',
              },
            }}
          />
        ) : (
          <Box className="p-12">
            <EmptyState
              Icon={History}
              title="No assistant actions"
              description="No change requests match the current filters."
            />
          </Box>
        )}
      </Box>

      <ActionHistoryDetailDialog action={selected} onClose={() => setSelected(null)} />
    </Box>
  );
};

export default AdminAgentHistoryPage;
